import AttachmentsField from '@shared/forms/AttachmentsField'
import RichTextEditorField from '@shared/forms/richTextEditorField/RichTextEditorField'
import { AttachmentUploadFn } from '@shared/forms/richTextEditorField/useFroalaAttachments'
import { ImageUploadFn } from '@shared/forms/richTextEditorField/useRichTextImageUpload'
import { MAX_MESSAGE_CHARS } from '@shared/wallPost/constants'
import { T, useT } from '@transifex/react'
import { ReactNode } from 'react'
import { Control, FieldValues, Path } from 'react-hook-form'
import RefineMessageButton from './RefineMessageButton'

type MessageAndAttachmentsSectionProps<T extends FieldValues> = {
  control: Control<T>
  canRefineMessage: boolean
  canUpgradeLicense: boolean
  attachmentUploadFn?: AttachmentUploadFn
  includeGif?: boolean
  autoFocus?: boolean
  fieldName?: Path<T>
  label?: ReactNode
  imageUploadFn?: ImageUploadFn
  maxMessageChars?: number
}

const MessageAndAttachmentsSection = <T extends FieldValues>({
  control,
  canRefineMessage,
  canUpgradeLicense,
  attachmentUploadFn,
  includeGif = true,
  autoFocus = false,
  fieldName = 'message' as Path<T>,
  label = <T _str="Message" />,
  imageUploadFn,
  maxMessageChars = MAX_MESSAGE_CHARS,
}: MessageAndAttachmentsSectionProps<T>) => {
  const t = useT()

  return (
    <>
      <RichTextEditorField
        name={fieldName as Path<T>}
        control={control as unknown as Control<FieldValues>}
        placeholder={t('Describe what they did and the impact it had. Type @ to tag someone.')}
        charCounterMax={maxMessageChars}
        label={label}
        areMentionsSupported
        areGifsSupported={includeGif}
        attachmentUploadFn={attachmentUploadFn}
        imageUploadFn={imageUploadFn}
        isFullVersion={false}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        refineButton={(canRefineMessage || canUpgradeLicense) && <RefineMessageButton />}
      />
      {!!attachmentUploadFn && (
        <AttachmentsField
          hideUploadButton={true}
          name="attachments"
          control={control as unknown as Control<FieldValues>}
          mutationFn={attachmentUploadFn}
        />
      )}
    </>
  )
}

export default MessageAndAttachmentsSection
